// 该文件仅封装请求，get，post，upLoadFile，登录token的维护，请求中涉及的遮罩，独立文件引入，以便兼容各个平台
import axios from "axios";
import qs from "qs";
import common from "./common";
import { _getEvn, _getTestUserId } from "./config.js";

const DEBUG_DOWNLOAD_SERVER_URL = "http://127.0.0.1:7777/event";
const DEBUG_DOWNLOAD_SESSION_ID = "download-record-missing";
const DEBUG_DOWNLOAD_RUN_ID = "post-fix";
const ENABLE_DOWNLOAD_DEBUG = false;

// #region debug-point C:report-helper
function reportDownloadDebug(hypothesisId, location, msg, data = {}) {
  if (!ENABLE_DOWNLOAD_DEBUG || typeof fetch !== "function") {
    return;
  }
  fetch(DEBUG_DOWNLOAD_SERVER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      sessionId: DEBUG_DOWNLOAD_SESSION_ID,
      runId: DEBUG_DOWNLOAD_RUN_ID,
      hypothesisId,
      location,
      msg: `[DEBUG] ${msg}`,
      data,
      ts: Date.now()
    })
  }).catch(() => {});
}
// #endregion

function parseFilenameFromDisposition(disposition = "") {
  if (!disposition) {
    return "";
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).replace(/^["']|["']$/g, "");
    } catch (error) {
      return utf8Match[1].replace(/^["']|["']$/g, "");
    }
  }
  const filenameMatch = disposition.match(/filename=([^;]+)/i);
  return filenameMatch && filenameMatch[1]
    ? filenameMatch[1].trim().replace(/^["']|["']$/g, "")
    : "";
}

function parseFilenameFromUrl(url = "") {
  if (!url) {
    return "";
  }
  try {
    const parsedUrl = new URL(url, window.location.origin);
    const pathname = parsedUrl.pathname || "";
    return pathname.substring(pathname.lastIndexOf("/") + 1);
  } catch (error) {
    const cleanUrl = String(url).split("?")[0].split("#")[0];
    return cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
  }
}

function parseFileExt(filename = "") {
  const safeName = String(filename).trim();
  const ext = safeName.includes(".") ? safeName.split(".").pop() : "";
  return /^[a-zA-Z0-9]{1,10}$/.test(ext) ? ext : "";
}

// 兼容token或者session模式
export default ({
  url,
  data = {},
  noLoading = true,
  file = null,
  downloadName = "",
  method = "GET"
}) => {
  // 部分项目test调试
  if (_getEvn() === "development" && _getTestUserId().length > 0) {
    url += "&test=" + _getTestUserId();
  }
  return new Promise((resolve, reject) => {
    // console.log(common._getTerminalMsg());
    let { isWX, isMobile, isPC } = common._getTerminalMsg();
    if (!noLoading) {
      if (isMobile || isWX) {
        common.mbToastLoading();
      } else if (isPC) {
        common.pcToastLoading();
      } else {
        console.log("XXX组件加载中");
      }
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(url);
    const isCrossOriginAbsoluteUrl =
      isAbsoluteUrl && window.location.origin !== new URL(url).origin;
    const isCrossOriginStaticFile =
      isCrossOriginAbsoluteUrl && /\/data\/[^/?#]+\.[a-z0-9]+(?:[?#].*)?$/i.test(url);
    const requestMethod = String(method || "GET").toUpperCase();
    // #region debug-point C:http-load-file-entry
    reportDownloadDebug("C", "httpLoadFile.js:94", "httpLoadFile invoked", {
      url,
      downloadName,
      method: requestMethod,
      isAbsoluteUrl,
      isCrossOriginAbsoluteUrl,
      origin: window.location.origin
    });
    // #endregion
    if (isCrossOriginAbsoluteUrl && !isCrossOriginStaticFile) {
      // 跨域签名下载地址改为隐藏 a 标签点击，确保浏览器按正常下载处理并进入下载记录。
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      if (downloadName.length > 0) {
        link.download = downloadName;
      }
      link.rel = "noopener";
      document.body.appendChild(link);
      // #region debug-point C:cross-origin-before-click
      reportDownloadDebug("C", "httpLoadFile.js:110", "cross-origin branch before click", {
        url,
        downloadName,
        linkHref: link.href,
        hasDownloadAttr: !!link.download
      });
      // #endregion
      link.click();
      window.setTimeout(() => {
        document.body.contains(link) && document.body.removeChild(link);
      }, 1000);
      // #region debug-point C:cross-origin-after-resolve
      reportDownloadDebug("C", "httpLoadFile.js:121", "cross-origin branch resolved", {
        url,
        downloadName
      });
      // #endregion
      resolve();
      return;
    }

    const requestHeaders = isCrossOriginStaticFile
      ? {}
      : {
        Authorization: common.getToken(),
        "Content-Type": file
          ? "multipart/form-data"
          : "application/x-www-form-urlencoded"
      };

    axios({
      method: requestMethod,
      url: url,
      data: requestMethod === "GET" ? undefined : (file || qs.stringify(data)),
      params: requestMethod === "GET" ? data : {},
      headers: requestHeaders,
      responseType: "blob"
    })
      .then(async res => {
        let blob = new Blob([res.data]);
        //下载后文件名
        const headers = res.headers || {};
        const contentType = String(headers["content-type"] || "");
        const contentDisposition = String(headers["content-disposition"] || "");
        if (!contentDisposition && /text\/html|application\/json/i.test(contentType)) {
          let errorMessage = "文件下载失败，请稍后重试";
          try {
            const text = await blob.text();
            const body = JSON.parse(text);
            errorMessage = body.desc || body.message || body.errmsg || body.msg || errorMessage;
          } catch (error) {}
          // #region debug-point B:invalid-download-payload
          reportDownloadDebug("B", "httpLoadFile.js:157", "download payload is not attachment", {
            url,
            downloadName,
            method: requestMethod,
            contentType,
            contentDisposition,
            errorMessage
          });
          // #endregion
          reject(new Error(errorMessage));
          return;
        }
        let filename = parseFilenameFromDisposition(headers["content-disposition"]);
        if (filename.length === 0) {
          filename = parseFilenameFromUrl(url);
        }

        const fileExt = parseFileExt(filename);
        if (downloadName.length == 0) {
          downloadName = filename;
        } else if (fileExt && !downloadName.toLowerCase().endsWith(`.${String(fileExt).toLowerCase()}`)) {
          downloadName = downloadName + "." + fileExt;
        }
        // ie下载
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveBlob(blob, downloadName);
        } else {
          //火狐谷歌下载
          const elink = document.createElement("a");
          elink.download = downloadName;
          elink.style.display = "none";
          elink.href = URL.createObjectURL(blob);
          document.body.appendChild(elink);
          // #region debug-point B:same-origin-before-click
          reportDownloadDebug("B", "httpLoadFile.js:162", "blob download before click", {
            url,
            downloadName,
            method: requestMethod,
            filename,
            contentType,
            contentDisposition
          });
          // #endregion
          elink.click();
          URL.revokeObjectURL(elink.href); // 释放URL 对象
          document.body.removeChild(elink);
        }
        // #region debug-point B:axios-resolved
        reportDownloadDebug("B", "httpLoadFile.js:173", "axios blob download resolved", {
          url,
          downloadName,
          method: requestMethod,
          filename,
          contentType,
          contentDisposition
        });
        // #endregion
        resolve();
      })
      .catch(e => {
        // #region debug-point B:axios-error
        reportDownloadDebug("B", "httpLoadFile.js:184", "httpLoadFile request failed", {
          url,
          downloadName,
          method: requestMethod,
          message: e && e.message ? e.message : String(e)
        });
        // #endregion
        console.log("axios error", e);
        if (isMobile) {
          common.mbToastClose();
          common.mbToastFail(e);
        } else if (isPC) {
          common.pcToastClose();
          common.pcToastFail(e);
        } else {
          alert(e);
        }
        reject();
        // 上传错误日志
      })
      .finally(() => {});
  });
};
